import * as React from 'react';
import { Theme, makeStyles, createStyles } from '@bb-ui/react-library';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { TabPanel } from '@bb-ui/react-library/dist/components/TabPanel';
import { useTranslation } from 'react-i18next';
import { Header } from 'components/Header';
import { ContentWrap } from 'components/ContentWrap';
import { PageTabs } from 'components/PageTabs';
import { PageTabProps } from 'components/PageTabs/PageTabs.types';
import { useAppRouteContext } from 'contexts/AppRouteContext';
import { PageCard } from 'components/PageCard';
import { ErrorMessage, ErrorMessageProps } from 'components/ErrorMessage';
import { BackNavigation, BackNavigationProps } from 'components/BackNavigation';
import { Breadcrumbs } from 'components/Breadcrumbs';
import { useExperience } from 'contexts/ExperienceContext';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      gridArea: 'page-header',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.default,
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
    },
    headerDetailWrap: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      paddingTop: theme.spacing(2),
    },
    headerDetail: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      fontWeight: 500,
      letterSpacing: '-0.02em',
    },
    tabsWrapper: {
      marginTop: theme.spacing(1.5),
      overflowX: 'auto',
    },
    rootClassic: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.default,
    },
    breadcrumbs: {
      margin: '12px 0 0 30px',
    },
    mainContent: {
      width: '100%',
      maxWidth: '100%',
      overflow: 'auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: theme.spacing(3),
    },
  }),
);

export interface PageTemplateProps {
  title: React.ReactNode;
  subtitle?: string;
  headerControl?: React.ReactNode;
  showTabs?: boolean;
  tabProps?: Partial<PageTabProps>;
  errorMessage?: ErrorMessageProps;
  unbounded?: boolean;
  disabledTabIds?: string[];
  breadcrumbProps?: BackNavigationProps | false;
  groupedTabs?: boolean;
}

export const PageTemplate: React.FunctionComponent<PageTemplateProps> = (props) => {
  const {
    children,
    showTabs,
    headerControl,
    subtitle,
    tabProps,
    title,
    errorMessage,
    disabledTabIds,
    breadcrumbProps,
    groupedTabs,
    ...other
  } = props;
  const { t } = useTranslation();
  const classes = useStyles(props);
  const { currentRoute } = useAppRouteContext();
  const { isClassic } = useExperience();
  // In classic mode, always show tabs for top-level navigation
  const effectiveShowTabs = showTabs || isClassic;
  const tabPanelId = 'page-template-tabs';
  let content = children;

  if (errorMessage) {
    content = (
      <PageCard>
        <ErrorMessage minHeight="168px" {...errorMessage} />
      </PageCard>
    );
  }

  if (effectiveShowTabs) {
    content = (
      <TabPanel
        panelId={tabPanelId}
        // Needs to match the DOM ID that will be assigned to the active tab by <PageTabs>
        ariaLabelledby={`tab-${currentRoute?.path}`}
        // tabindex={-1} // TODO: update in bb-ui to accept additional props and should default to -1
      >
        {content}
      </TabPanel>
    );
  }

  // Render the title as ReactNode or as Typography if it's a simple string
  const titleElement =
    typeof title === 'string' ? (
      <Typography variant="display2" component="h2" className={classes.title}>
        {title}
      </Typography>
    ) : (
      title
    );

  if (isClassic) {
    return (
      <>
        <Header />
        <div className={classes.rootClassic} data-testid="page-header">
          <Breadcrumbs
            className={classes.breadcrumbs}
            menuId="breadcrumbs-menu"
            menuButtonLabel={t('global.navigation')}
          />
          <ContentWrap className={classes.headerDetailWrap}>
            <div className={classes.headerDetail}>
              <div>
                {titleElement}
                {subtitle && (
                  <Typography variant="body2" color="textSecondary">
                    {subtitle}
                  </Typography>
                )}
              </div>
              {headerControl}
            </div>
            {effectiveShowTabs && (
              <PageTabs
                tabPanelId={tabPanelId}
                tabProps={tabProps}
                disabledTabIds={disabledTabIds}
              />
            )}
          </ContentWrap>
        </div>
        <ContentWrap component="main" marginY="50px" {...other}>
          {content}
        </ContentWrap>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className={classes.root} data-testid="page-header">
        <ContentWrap className={classes.headerDetailWrap}>
          {breadcrumbProps && <BackNavigation {...breadcrumbProps} />}
          {!breadcrumbProps && <BackNavigation />}
          <div className={classes.headerDetail}>
            <div>
              {titleElement}
              {subtitle && (
                <Typography variant="body2" color="textSecondary">
                  {subtitle}
                </Typography>
              )}
            </div>
            {headerControl}
          </div>
          {showTabs && (
            <div className={classes.tabsWrapper}>
              <PageTabs
                tabPanelId={tabPanelId}
                tabProps={tabProps}
                disabledTabIds={disabledTabIds}
                groupedTabs={groupedTabs}
              />
            </div>
          )}
        </ContentWrap>
      </div>
      <ContentWrap className={classes.mainContent}>{content}</ContentWrap>
    </>
  );
};

export default PageTemplate;
